import { Link } from "react-router-dom";
import useSWR from "swr";
import {
  ArrowRight,
  ArrowUpRight,
  PlayCircle,
  Beaker,
  Factory,
  Sparkles,
  Package,
  ShieldCheck,
  Globe,
  Rocket,
  ScrollText,
  Clock,
  BadgeCheck,
  CalendarDays,
  Quote,
} from "lucide-react";
import { fetcher } from "@/lib/api";
import OrbitalScene from "@/components/OrbitalScene";
import { Reveal, CountUp } from "@/components/Reveal";
import { BookMeeting } from "@/components/BookMeeting";
import { LabelModels } from "@/components/LabelModels";
import { FOUNDERS, FounderCard } from "@/components/Founders";

const STAT_CHIPS = [
  { to: 600, s: "+", v: "Formulations" },
  { to: 72, s: " Hrs", v: "Brand Launch" },
  { to: 500, s: "+", v: "Clients Worldwide" },
  { to: 30, s: "+", v: "Countries Served" },
];

const WHY_ITEMS = [
  { icon: Factory, title: "OEM Manufacturing", note: "Contract production at GMP scale" },
  { icon: BadgeCheck, title: "Private Label", note: "Your brand, our science" },
  { icon: Sparkles, title: "Custom R&D", note: "Bespoke formula development" },
  { icon: Package, title: "Packaging", note: "In-house design studio" },
  { icon: ShieldCheck, title: "Regulatory", note: "FDA · CDSCO · GMP" },
  { icon: Rocket, title: "Low MOQ", note: "2,000 units India · 5,000 Global" },
  { icon: Globe, title: "Global Export", note: "Ship to 30+ countries" },
  { icon: Clock, title: "365 Support", note: "Round-the-year partner" },
];

const PROCESS = [
  { n: "01", t: "Idea", d: "You bring the vision — we decode the audience." },
  { n: "02", t: "Consultation", d: "1-to-1 session with formulators & brand strategists." },
  { n: "03", t: "Formula", d: "Pick from 600+ or co-create a custom formula." },
  { n: "04", t: "Packaging", d: "Design studio for bottles, labels, secondary packs." },
  { n: "05", t: "Manufacturing", d: "GMP facility · precision blending · QA at every stage." },
  { n: "06", t: "Brand Launch", d: "Ready-to-ship in 72 hours. World stage awaits." },
];

const SNAPSHOT = [
  { to: 625, p: "$", s: "B", v: "Global beauty market by 2030", note: "Statista · 2026" },
  { to: 15, s: "%", v: "India CAGR — 3× global average", note: "Redseer · 2025" },
  { to: 35, s: "%", v: "D2C beauty growth — fastest segment", note: "Nielsen · 2025" },
  { to: 72, s: " Hrs", v: "Fresio launch vs 6-month industry norm", note: "Fresio · 2026" },
];

const GROWTH = [
  { k: "2,000", v: "Units MOQ — India · 5,000 Global", accent: true },
  { k: "₹7 Lac+", v: "Starting brand-launch budget", accent: true },
  { k: "200+", v: "New brands launched / quarter" },
  { k: "72 Hrs", v: "Sample delivery guarantee" },
  { k: "4× YoY", v: "Growth in 2025 alone" },
];

const TESTIMONIALS = [
  { q: "Fresio compressed six months of work into three days. We hit ₹1Cr revenue in 90 days after launch — impossible without their R&D lab.", n: "Ananya Kapoor", r: "Founder · Skinsense" },
  { q: "The formula lab and packaging team feel like our own — not a vendor. Weekly reviews, live iteration, zero surprises. Rare in this industry.", n: "Rohan Mehta", r: "Founder · Botanica Men's" },
  { q: "Regulatory paperwork for our UAE launch was ready before we even asked. Zero customs holdups. First order shipped in week two.", n: "Sarah Al-Farsi", r: "CEO · Kohl & Cream, Dubai" },
];

const TICKER = ["OEM Manufacturing", "Private Label", "600+ Proven Formulas", "GMP · ISO Certified", "30+ Countries", "72 Hour Brand Launch", "R&D Custom Formulation", "Export Ready"];

function SectionHead({ n, eyebrow, title, accent, aside, dark }) {
  return (
    <Reveal className="grid lg:grid-cols-12 gap-8 mb-16 items-end">
      <div className="lg:col-span-7">
        <div className={`flex items-center gap-4 mb-6 ${dark ? "text-[#E6D5A4]" : "text-[#A68840]"}`}>
          <span className="font-editorial italic text-2xl">{n}</span>
          <span className="h-px w-12 bg-current opacity-60" />
          <span className="text-[11px] tracking-[0.3em] uppercase font-medium">{eyebrow}</span>
        </div>
        <h2 className={`font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.02em] ${dark ? "text-[#FDFBF7]" : "text-[#1D3A2F]"}`}>
          {title}
          <br />
          <em className="gold-text">{accent}</em>
        </h2>
      </div>
      {aside && (
        <div className="lg:col-span-4 lg:col-start-9">
          <div className={`text-base lg:text-lg leading-relaxed ${dark ? "text-[#FDFBF7]/70" : "text-[#222]/70"}`}>{aside}</div>
        </div>
      )}
    </Reveal>
  );
}

function SpinBadge() {
  const text = "FIRST IN INDIA · 72 HOUR BRAND LAUNCH · ";
  return (
    <div className="absolute -left-4 top-8 lg:left-0 w-32 h-32 lg:w-40 lg:h-40 z-20" aria-hidden="true">
      <svg viewBox="0 0 200 200" className="w-full h-full animate-spin-slow" style={{ animationDuration: "22s" }}>
        <defs>
          <path id="circle-path" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
        </defs>
        <text fill="#A68840" fontSize="13" letterSpacing="2.6" fontFamily="Poppins" fontWeight="600">
          <textPath href="#circle-path">{text}</textPath>
        </text>
      </svg>
      <div className="absolute inset-0 m-auto w-14 h-14 lg:w-16 lg:h-16 rounded-full btn-gold flex items-center justify-center">
        <Sparkles className="w-6 h-6" />
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section data-testid="hero" className="relative overflow-hidden hero-mesh grain">
      <div className="absolute top-24 right-[8%] w-[520px] h-[520px] rounded-full border gold-border opacity-60 animate-spin-slow hidden lg:block" />
      <div className="absolute -top-20 left-[40%] w-72 h-72 rounded-full bg-[#E6D5A4]/40 blur-3xl animate-drift" />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 animate-fade-up">
            <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-pulse" />
            <span className="text-[10px] sm:text-[11px] tracking-[0.26em] uppercase text-[#1D3A2F] font-semibold">India's Fastest Brand Launch System</span>
          </div>
          <h1 className="font-editorial text-[56px] sm:text-[88px] lg:text-[118px] leading-[0.88] tracking-[-0.035em] text-[#1D3A2F]">
            <span className="block animate-fade-up">Launch your</span>
            <span className="block animate-fade-up" style={{ animationDelay: "0.12s" }}>cosmetic brand</span>
            <span className="flex items-baseline gap-4 animate-fade-up" style={{ animationDelay: "0.24s" }}>
              <em className="text-[#556B57] text-[0.62em]">in just</em>
              <span className="gold-text italic font-semibold text-[1.25em] leading-none">72</span>
              <span className="text-outline-gold italic">hours.</span>
            </span>
          </h1>
          <p className="mt-10 text-base sm:text-lg max-w-xl text-[#222]/75 leading-relaxed animate-fade-up" style={{ animationDelay: "0.36s" }}>
            From idea to global beauty brand — formulation, packaging, manufacturing and branding under one roof. Engineered for founders who move at the speed of the market.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "0.48s" }}>
            <Link to="/order-sample" data-testid="hero-cta-launch" className="btn-gold group inline-flex items-center gap-3 h-14 pl-7 pr-2 rounded-full text-[12px] tracking-[0.18em] uppercase font-bold">
              Launch My Brand
              <span className="w-10 h-10 rounded-full bg-[#1D3A2F] text-[#F4E4BD] flex items-center justify-center transition-transform duration-500 group-hover:rotate-[-45deg]">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <a href="#book-meeting" data-testid="hero-cta-consult" className="inline-flex items-center gap-3 h-14 px-7 rounded-full border border-[#1D3A2F]/25 bg-white/40 backdrop-blur text-[#1D3A2F] text-[12px] tracking-[0.18em] uppercase font-medium hover:border-[#C9A961] transition-colors">
              <PlayCircle className="w-4 h-4" /> Book Consultation
            </a>
          </div>
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-[#C9A961]/30 pt-8 animate-fade-up" style={{ animationDelay: "0.6s" }}>
            {STAT_CHIPS.map((s) => (
              <div key={s.v} data-testid={`hero-stat-${s.v.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="font-editorial text-4xl lg:text-5xl text-[#1D3A2F]"><CountUp to={s.to} suffix={s.s} /></div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#A68840] mt-1 font-medium">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <SpinBadge />
          <OrbitalScene className="w-full h-[440px] sm:h-[560px] lg:h-[660px] animate-float-slow" />
          <div className="absolute bottom-16 -left-2 lg:-left-10 glass-card rounded-2xl px-5 py-4 animate-float-med shadow-xl">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#556B57]">Minimum order</div>
            <div className="font-editorial text-3xl text-[#1D3A2F]">2,000 <span className="text-base">units</span></div>
            <div className="text-[10px] tracking-[0.16em] uppercase text-[#A68840]">India · 5,000 Global</div>
          </div>
          <div className="absolute top-24 -right-2 lg:right-0 glass-card rounded-2xl px-5 py-4 animate-float-x shadow-xl">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#556B57]">Sample in</div>
            <div className="font-editorial text-3xl gold-text">72 hrs</div>
          </div>
        </div>
      </div>
      <div data-testid="home-ticker" className="relative bg-[#1D3A2F] py-5 overflow-hidden border-y-2 border-[#C9A961]/60">
        <div className="animate-marquee flex whitespace-nowrap w-max">
          {[0, 1].map((i) => (
            <div key={i} className="flex">
              {TICKER.map((t) => (
                <span key={t} className="flex items-center gap-8 px-8 font-editorial italic text-2xl text-[#F4E4BD]">
                  {t} <Sparkles className="w-4 h-4 text-[#C9A961]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyFresio() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-32 lg:py-40">
      <SectionHead n="01" eyebrow="Why Fresio" title="More than a manufacturer," accent="your growth partner." aside="We compress six months of brand-building into 72 hours — with museum-grade formulations, precision manufacturing and a cross-border support system trusted by 500+ founders." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Reveal className="col-span-2 lg:row-span-2 rounded-[2rem] bg-[#1D3A2F] text-[#FDFBF7] p-10 relative overflow-hidden min-h-[360px] flex flex-col justify-between" data-testid="why-item-formulations">
          <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[#C9A961]/25 blur-3xl animate-drift" />
          <div className="absolute right-8 top-8 w-28 h-28 rounded-full border gold-border animate-spin-slow" />
          <Beaker className="w-8 h-8 text-[#C9A961]" strokeWidth={1.3} />
          <div className="relative">
            <div className="font-editorial text-[110px] lg:text-[150px] leading-none gold-text"><CountUp to={600} suffix="+" /></div>
            <div className="font-editorial text-3xl mt-2">Ready formulations</div>
            <p className="text-sm text-[#FDFBF7]/65 mt-3 max-w-sm">A proven, stability-tested library across skin, hair, body, sun, baby, men's and lip care.</p>
          </div>
        </Reveal>
        {WHY_ITEMS.map((it, i) => {
          const Icon = it.icon;
          return (
            <Reveal key={it.title} delay={i * 60} data-testid={`why-item-${it.title.toLowerCase().replace(/\s/g, "-")}`}
              className="group rounded-[2rem] bg-[#FDFBF7] p-7 border border-[#D7D4CF]/70 gold-hover-border">
              <div className="w-12 h-12 rounded-2xl bg-[#F7F4EE] flex items-center justify-center mb-8 border border-[#C9A961]/30 transition-transform duration-500 group-hover:rotate-12 group-hover:bg-[#C9A961]/15">
                <Icon className="w-5 h-5 text-[#1D3A2F]" strokeWidth={1.4} />
              </div>
              <div className="font-editorial text-2xl text-[#1D3A2F] leading-tight"><span className="gold-underline">{it.title}</span></div>
              <div className="text-sm text-[#556B57] mt-2">{it.note}</div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="bg-[#1D3A2F] text-[#FDFBF7] py-32 lg:py-40 relative overflow-hidden">
      <div className="absolute -right-24 top-10 font-editorial italic text-[360px] lg:text-[520px] leading-none text-outline-gold opacity-40 select-none pointer-events-none">72</div>
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#C9A961]/10 blur-3xl" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 relative">
        <SectionHead dark n="02" eyebrow="Brand Launch Process" title="Idea to shelf." accent="In seventy-two hours." aside="Six precision stages, one dedicated launch team. Every hour is accounted for — from your first call to dispatch." />
        <Reveal className="relative">
          <div className="hidden lg:block absolute top-6 left-0 right-0 h-px bg-[#FDFBF7]/10">
            <div className="h-px bg-gradient-to-r from-[#C9A961] via-[#F4E4BD] to-[#C9A961] animate-line-grow" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-6">
            {PROCESS.map((p, i) => (
              <div key={p.n} data-testid={`process-step-${p.n}`} className="relative group" style={{ marginTop: `${(i % 2) * 2.5}rem` }}>
                <div className="w-12 h-12 rounded-full bg-[#1D3A2F] border-2 border-[#C9A961] flex items-center justify-center font-editorial text-xl text-[#F4E4BD] relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:bg-[#C9A961] group-hover:text-[#1D3A2F]">
                  {i + 1}
                </div>
                <div className="mt-6 rounded-3xl bg-[#FDFBF7]/[0.04] border border-[#FDFBF7]/10 p-6 backdrop-blur-sm transition-colors duration-500 group-hover:border-[#C9A961]/50">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">Stage {p.n}</div>
                  <div className="font-editorial text-3xl mt-2">{p.t}</div>
                  <p className="text-sm text-[#FDFBF7]/60 mt-3 leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CategoriesShowcase({ categories }) {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-32 lg:py-40">
      <SectionHead n="03" eyebrow="Categories" title="Explore our" accent="product universe."
        aside={<Link to="/categories" data-testid="home-view-all-categories" className="btn-gold inline-flex items-center gap-2 h-12 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-bold">View All Categories <ArrowUpRight className="w-4 h-4" /></Link>} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
        {(categories || []).map((c, i) => {
          const span = i === 0 ? "lg:col-span-7 lg:row-span-2" : i === 1 || i === 2 ? "lg:col-span-5" : "lg:col-span-3";
          const h = i === 0 ? "h-[420px] lg:h-full min-h-[420px]" : i < 3 ? "h-[260px]" : "h-[300px]";
          return (
            <Reveal key={c.slug} delay={i * 70} className={span}>
              <Link to={`/categories/${c.slug}`} data-testid={`category-card-${c.slug}`} className={`group block relative overflow-hidden rounded-[2rem] ${h}`}>
                <img src={c.hero_image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F251C]/90 via-[#1D3A2F]/30 to-transparent" />
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#C9A961]/70 rounded-[2rem] transition-colors duration-500" />
                <span className="absolute top-6 left-6 font-editorial italic text-xl text-[#F4E4BD]">{String(i + 1).padStart(2, "0")}</span>
                <span className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[#FDFBF7] flex items-center justify-center transition-[transform,background-color] duration-500 group-hover:rotate-45 group-hover:bg-[#C9A961]">
                  <ArrowUpRight className="w-4 h-4 text-[#1D3A2F]" />
                </span>
                <div className="absolute inset-x-6 bottom-6 text-[#FDFBF7]">
                  <h3 className={`font-editorial leading-none ${i === 0 ? "text-5xl lg:text-7xl" : "text-3xl"}`}>{c.name}</h3>
                  <p className="text-sm text-[#FDFBF7]/70 mt-2 line-clamp-1">{c.tagline}</p>
                  {c.market_size && <div className="mt-3 inline-block text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-[#C9A961]/60 text-[#F4E4BD]">{c.market_size}</div>}
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function Bestsellers({ items }) {
  return (
    <section className="bg-[#FDFBF7] py-32 border-y border-[#C9A961]/25 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <SectionHead n="04" eyebrow="Bestsellers" title="Shop trending" accent="formulas."
          aside={<Link to="/order-sample" className="group inline-flex items-center gap-2 text-sm tracking-[0.18em] uppercase text-[#1D3A2F] font-medium"><span className="gold-underline">Order a sample</span> <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link>} />
      </div>
      <div className="flex gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar px-6 lg:px-[max(2.5rem,calc((100vw-1440px)/2+2.5rem))] pb-4" data-testid="bestsellers-rail">
        {(items || []).map((p, i) => (
          <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`bestseller-card-${p.slug}`}
            className="group snap-start shrink-0 w-[260px] sm:w-[320px]" style={{ marginTop: i % 2 ? "3rem" : 0 }}>
            <div className="relative rounded-[2rem] overflow-hidden bg-[#F7F4EE] aspect-[3/4] mb-5 border border-[#D7D4CF]/60 group-hover:border-[#C9A961] transition-colors duration-500">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
              <span className="absolute top-4 left-4 text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full glass-card text-[#1D3A2F] font-semibold">MOQ {(p.moq || 2000).toLocaleString("en-IN")}</span>
            </div>
            <div className="font-editorial text-2xl text-[#1D3A2F] leading-tight"><span className="gold-underline">{p.name}</span></div>
            <div className="text-xs text-[#556B57] mt-2 line-clamp-2">{p.tagline}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Snapshot() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-32 lg:py-40">
      <SectionHead n="05" eyebrow="Industry Snapshot" title="The beauty market has" accent="never moved this fast."
        aside={<>India's beauty market is on track to hit <b>$32B by 2027</b>, growing at <b>15% CAGR</b> — 3× the global average. The window for new brands is wide open.</>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-[#C9A961]/40">
        {SNAPSHOT.map((s, i) => (
          <Reveal key={s.v} delay={i * 100} className={`py-10 pr-6 ${i ? "lg:pl-8 lg:border-l border-[#C9A961]/30" : ""} ${i % 2 ? "pl-6 border-l border-[#C9A961]/30 lg:pl-8" : ""}`}>
            <div className="font-editorial text-6xl lg:text-7xl text-[#1D3A2F] tracking-tight"><CountUp to={s.to} prefix={s.p || ""} suffix={s.s} /></div>
            <div className="text-sm text-[#1D3A2F] mt-5 leading-snug max-w-[220px]">{s.v}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#A68840] mt-3">{s.note}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FastestGrowing() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-32">
      <Reveal className="rounded-[2.5rem] overflow-hidden relative bg-gradient-to-br from-[#1D3A2F] via-[#2A4E3F] to-[#0F251C] p-8 sm:p-12 lg:p-16 gold-glow">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#C9A961]/25 blur-3xl animate-drift" />
        <div className="absolute top-10 right-16 w-40 h-40 rounded-full border gold-border animate-spin-slow" />
        <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-[#FDFBF7]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border gold-border bg-[#C9A961]/10 mb-6 animate-glow-pulse">
              <Sparkles className="w-3.5 h-3.5 text-[#C9A961]" />
              <span className="text-[10px] tracking-[0.22em] uppercase gold-text font-bold">India's #1 Private Label</span>
            </div>
            <h2 className="font-editorial text-5xl lg:text-7xl leading-[0.98]">
              India's Fastest Growing <br /><em className="gold-text">Private Label Manufacturing</em> <br />Unit.
            </h2>
            <p className="mt-8 max-w-xl text-base lg:text-lg text-[#FDFBF7]/80 leading-relaxed">
              From a single-product R&amp;D lab in 2019 to a 40,000 sq ft powerhouse manufacturing for <b>500+ beauty brands</b> across 30+ countries — Fresio adds <span className="gold-text font-semibold">200+ new brand launches every quarter</span> and grew <b>4× year-on-year</b> in 2025.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/order-sample" data-testid="fastest-growing-start" className="btn-gold inline-flex items-center gap-3 h-14 px-7 rounded-full text-[12px] tracking-[0.16em] uppercase font-bold">
                Start Your Brand <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#book-meeting" data-testid="fastest-growing-office-visit" className="inline-flex items-center gap-3 h-14 px-7 rounded-full border border-[#FDFBF7]/25 text-[#FDFBF7] text-[12px] tracking-[0.16em] uppercase hover:border-[#C9A961] transition-colors">
                Book Office Visit
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 space-y-3">
            {GROWTH.map((s, i) => (
              <div key={s.v} style={{ marginLeft: `${(i % 3) * 1.25}rem` }}
                className={`rounded-2xl p-5 flex items-center justify-between transition-transform duration-500 hover:-translate-x-2 ${s.accent ? "bg-[#C9A961]/10 border gold-border" : "bg-[#FDFBF7]/5 border border-[#FDFBF7]/10"}`}>
                <div>
                  <div className={`font-editorial text-3xl ${s.accent ? "gold-text" : "text-[#FDFBF7]"}`}>{s.k}</div>
                  <div className="text-[11px] tracking-[0.14em] uppercase text-[#FDFBF7]/60 mt-1">{s.v}</div>
                </div>
                {s.accent && <Sparkles className="w-4 h-4 text-[#C9A961] animate-pulse" />}
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function LabelSection() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-32" data-testid="home-label-models">
      <SectionHead n="06" eyebrow="Private Label · White Label" title="Two ways to launch." accent="One trusted partner."
        aside="Build a signature formula that's exclusively yours, or launch fast with a proven formula under your brand. Either way, Fresio handles formulation, packaging, manufacturing and compliance." />
      <LabelModels />
    </section>
  );
}

function MeetingSection() {
  return (
    <section id="book-meeting" data-testid="home-book-meeting" className="relative py-32 lg:py-40 hero-mesh scroll-mt-24">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <SectionHead n="07" eyebrow="Book a Meeting" title="Talk to a launch" accent="expert — free."
          aside={<span className="flex items-start gap-3"><CalendarDays className="w-5 h-5 mt-1 text-[#A68840] shrink-0" />30-minute strategy session — formulation review, launch plan and MOQ discussion. Pick a slot that suits you; we'll confirm instantly.</span>} />
        <Reveal className="rounded-[2.5rem] glass-card p-6 sm:p-10 lg:p-14 shadow-[0_40px_80px_-40px_rgba(166,136,64,0.45)]">
          <BookMeeting />
        </Reveal>
      </div>
    </section>
  );
}

function GlobalPresence() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-32">
      <Reveal className="rounded-[2.5rem] bg-[#1D3A2F] p-8 sm:p-12 lg:p-16 relative overflow-hidden">
        <Globe className="absolute -right-20 -bottom-20 w-[420px] h-[420px] text-[#C9A961]/10 animate-spin-slow" strokeWidth={0.5} />
        <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 text-[#FDFBF7]">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#E6D5A4] mb-5">08 · Global Presence</div>
            <h2 className="font-editorial text-5xl lg:text-6xl leading-[1.02]">Trusted by brands <br /><em className="gold-text">around the globe.</em></h2>
            <p className="mt-6 max-w-md text-[#FDFBF7]/70 leading-relaxed">Export documentation, regulatory support and cross-border logistics trusted by 500+ partners in 30+ countries.</p>
          </div>
          <div className="lg:col-span-6">
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[{ to: 30, s: "+", v: "Countries" }, { to: 500, s: "+", v: "Brands" }, { to: 12, s: "M+", v: "Products" }].map((x) => (
                <div key={x.v}>
                  <div className="font-editorial text-4xl lg:text-5xl gold-text"><CountUp to={x.to} suffix={x.s} /></div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[#FDFBF7]/60 mt-1">{x.v}</div>
                </div>
              ))}
            </div>
            <Link to="/contact" data-testid="home-become-partner" className="btn-gold inline-flex items-center gap-3 h-14 px-7 rounded-full text-[12px] tracking-[0.18em] uppercase font-bold">
              Become Our Partner <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function Founder() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-32">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className="lg:col-span-6 grid grid-cols-2 gap-5 sm:gap-8">
          {FOUNDERS.map((f, i) => (
            <div key={f.id} className={i ? "mt-12" : ""}>
              <FounderCard f={f} testid={`home-founder-${f.id}`} tilt={i ? "-rotate-3" : "rotate-3"} />
            </div>
          ))}
        </Reveal>
        <Reveal delay={150} className="lg:col-span-6">
          <Quote className="w-14 h-14 text-[#C9A961] mb-6" strokeWidth={1} />
          <blockquote className="font-editorial text-4xl md:text-5xl leading-[1.08] text-[#1D3A2F] tracking-[-0.01em]">
            At Fresio, we don't just build brands — we build <em className="gold-text">confidence, clarity and possibilities</em> for every individual who dares to dream.
          </blockquote>
          <div className="mt-8 flex items-center gap-4 text-sm text-[#556B57]"><span className="h-px w-12 bg-[#C9A961]" /> Mr. Sumit Lathia &amp; Mr. Mitesh Patel, Founders</div>
        </Reveal>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-32">
      <SectionHead n="09" eyebrow="Founder Voices" title="Brands that launched" accent="with Fresio." aside="500+ founders across 30+ countries. Every story starts with a sample — and ends on a global shelf." />
      <div className="grid md:grid-cols-3 gap-6 items-start">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.n} delay={i * 120} className={`rounded-[2rem] p-8 border gold-hover-border ${i === 1 ? "bg-[#1D3A2F] text-[#FDFBF7] border-[#1D3A2F] md:mt-12" : "bg-[#FDFBF7] border-[#D7D4CF]/70"}`}>
            <div className="font-editorial text-7xl leading-none gold-text">“</div>
            <blockquote className={`font-editorial text-xl leading-snug mt-2 ${i === 1 ? "" : "text-[#1D3A2F]"}`}>{t.q}</blockquote>
            <div className={`mt-8 pt-6 border-t ${i === 1 ? "border-[#FDFBF7]/15" : "border-[#D7D4CF]/60"}`}>
              <div className="font-medium">{t.n}</div>
              <div className={`text-sm mt-0.5 ${i === 1 ? "text-[#E6D5A4]" : "text-[#556B57]"}`}>{t.r}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CTAStrip() {
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-24">
      <Reveal className="rounded-[2.5rem] border-2 border-[#C9A961]/50 bg-[#FDFBF7] p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center relative overflow-hidden">
        <div className="absolute -right-10 -top-10 font-editorial italic text-[220px] leading-none text-outline-gold opacity-50 select-none">72</div>
        <div className="lg:col-span-7 relative">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A68840] mb-4">Start today</div>
          <h3 className="font-editorial text-5xl md:text-6xl text-[#1D3A2F] leading-[1.02]">Your idea deserves the world. <br /><em className="gold-text">Let us take it there.</em></h3>
        </div>
        <div className="lg:col-span-5 flex flex-wrap gap-3 relative">
          <Link to="/order-sample" data-testid="cta-order-sample" className="btn-gold inline-flex items-center gap-3 h-14 px-7 rounded-full text-[12px] tracking-[0.16em] uppercase font-bold">
            <ScrollText className="w-4 h-4" /> Order Sample
          </Link>
          <a href="#book-meeting" data-testid="cta-talk-expert" className="inline-flex items-center gap-3 h-14 px-7 rounded-full border border-[#1D3A2F]/30 text-[#1D3A2F] text-[12px] tracking-[0.16em] uppercase font-medium hover:border-[#C9A961] transition-colors">
            Talk to Expert <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const { data: categories } = useSWR("/categories", fetcher);
  const { data: bestSellers } = useSWR("/products?best_seller=true", fetcher);

  return (
    <div className="overflow-x-hidden">
      <Hero />
      <WhyFresio />
      <Process />
      <CategoriesShowcase categories={categories} />
      <Bestsellers items={bestSellers} />
      <Snapshot />
      <FastestGrowing />
      <LabelSection />
      <MeetingSection />
      <GlobalPresence />
      <Founder />
      <Testimonials />
      <CTAStrip />
    </div>
  );
}
